import { change, initialize } from 'redux-form';
import { call, cancel, put, takeEvery, takeLatest } from 'redux-saga/effects';
import forEach from 'lodash/forEach';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';

import * as act from '../../actions';
import * as actEmployeeList from '../../actions/employees/list';
import * as actNotif from '../../actions/notifications';
import {
  EMPLOYEE_LIST_ASK_DELETE_EMPLOYEE,
  EMPLOYEE_LIST_ASSIGN_OUTLETS,
  EMPLOYEE_LIST_CANCEL_ASSIGN_OUTLETS,
  EMPLOYEE_LIST_CANCEL_DELETE_EMPLOYEE,
  EMPLOYEE_LIST_CHECK_ALL_OUTLETS,
  EMPLOYEE_LIST_CONFIRM_ASSIGN_OUTLETS,
  EMPLOYEE_LIST_CONFIRM_DELETE_EMPLOYEE,
  EMPLOYEE_LIST_PAGE_REQUEST,
  EMPLOYEE_LIST_RESEND_INVITATION,
  EMPLOYEE_LIST_SEARCH_OUTLET,
  EMPLOYEE_LIST_SUBMIT_FORM,
  EMPLOYEE_LIST_UNCHECK_ALL_OUTLETS,
} from '../../actions/employees/list/constants';
import { LOGOUT } from '../../actions/constants';
import { showPageLoadingSpinner } from '../../actions/loadingSpinner';

import * as apiEmployee from '../../api/employees';
import * as apiOutlets from '../../api/outlets';
import * as apiProfile from '../../api/profile';
import { assertErrorHasMessage } from '../../utils/assert';

function generateRandomPassword() {
  const passwordLen = 6;
  let text = '';
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  for (let i = 0; i < passwordLen; ++i) {
    text += possible.charAt(Math.floor(Math.random() * possible.length));
  }
  return text;
}

function reorderEmployees(employees) {
  const pendingEmployees: any[] = [];
  const confirmedEmployees: any[] = [];
  employees.forEach((e) => {
    if (e.is_confirmed) {
      confirmedEmployees.push(e);
    }
  });
  employees.forEach((e) => {
    if (!e.is_confirmed) {
      pendingEmployees.push(e);
    }
  });
  return confirmedEmployees.concat(pendingEmployees);
}

function reorderStaff(staffs) {
  const pendingEmployees: any[] = [];
  const confirmedEmployees: any[] = [];
  // staffs index 0 always manager that currently logged in
  const manager = staffs[0];
  const managerOutletId: any[] = [];
  forEach(manager.outlets, (outlet) => {
    managerOutletId.push(outlet.id);
  });
  forEach(staffs, (stf) => {
    let user = stf;
    const userAssignedOutlet: any[] = [];

    forEach(user.outlets, (outlet) => {
      if (includes(managerOutletId, outlet.id)) userAssignedOutlet.push(outlet);
    });
    if (isEmpty(userAssignedOutlet)) user = null;

    if (
      user &&
      !user.is_deleted &&
      user.outlets.length !== 0 &&
      !user.is_manager
    ) {
      user.outlets = userAssignedOutlet;
      user.title = user.employee_business.employee_title;
      if (user.is_confirmed) {
        confirmedEmployees.push(user);
      } else {
        pendingEmployees.push(user);
      }
    }
  });
  return confirmedEmployees.concat(pendingEmployees);
}

function isJSONFromAssignStaff(employee) {
  return Object.prototype.hasOwnProperty.call(employee, 'user_id');
}

export function* fillForm(outlets) {
  const form = {
    password: generateRandomPassword(),
    outlets: outlets.map((o) => {
      return { id: o.id, name: o.name, checked: false, show: true };
    }),
  };

  yield put(initialize('invite', form));
}

export function* resetCheckedOutlets(outlets) {
  const checkedOutlets = outlets.map((o) => {
    return { id: o.id, name: o.name, checked: false, show: true };
  });
  yield put(actEmployeeList.setCheckedOutlets(checkedOutlets));
}

export function* pageRequest() {
  yield put(actEmployeeList.setEmployees([]));
  yield put(showPageLoadingSpinner(true));
  try {
    yield put(act.sendingRequest(true));
    yield put(act.showProgressBar());

    // call api profile since we needed it for permission (if manager can only invite as app only)
    const resProfile = yield call(apiProfile.getProfile);
    yield put(actEmployeeList.setProfile(resProfile.data));

    if (resProfile.data.is_owner) {
      const { data } = yield call(apiEmployee.getEmployees, {});
      // reorder
      const orderedEmployees = reorderEmployees(data);
      yield put(actEmployeeList.setEmployees(orderedEmployees));
    } else {
      const { data } = yield call(apiEmployee.getAssignedStaffV1, {});
      const orderedStaff = reorderStaff(data);
      yield put(actEmployeeList.setEmployees(orderedStaff));
    }

    // call api outlet
    const response = yield call(apiOutlets.outlets);
    yield put(actEmployeeList.setOutlets(response.data));

    // reset checked outlets
    yield call(resetCheckedOutlets, response.data);

    yield call(fillForm, response.data);
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
    yield put(act.sendingRequest(false));
    yield put(showPageLoadingSpinner(false));
  }
}

export function* resendInvitation(request) {
  // noinspection InfiniteLoopJS
  const {
    payload: { email },
  } = request;
  try {
    yield put(act.sendingRequest(true));
    yield put(act.showProgressBar());
    yield call(
      apiEmployee.resendInvitation,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      { pending_employee_email: email },
      {}
    );
    // Please git blame api.js at line 2681 on moka-rails to see why this message is hard-coded
    yield put(actNotif.success('Employee invitation sent.'));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
    yield put(act.sendingRequest(false));
  }
}

export function* cancelDeleteEmployee() {
  yield put(actEmployeeList.setDeletedEmployee(null));
  yield put(actEmployeeList.hideDeleteEmployeeModal());
}

export function* askDeleteEmployee(request) {
  const {
    payload: { deletedEmployee },
  } = request;
  yield put(actEmployeeList.setDeletedEmployee(deletedEmployee));
  yield put(actEmployeeList.showDeleteEmployeeModal());
}

export function* confirmDeleteEmployee(request) {
  const {
    payload: { deletedEmployee },
  } = request;
  try {
    yield put(act.sendingRequest(true));
    yield put(act.showProgressBar());
    const userId = isJSONFromAssignStaff(deletedEmployee)
      ? deletedEmployee.user_id
      : deletedEmployee.id;
    yield call(apiEmployee.deleteEmployee, userId, {});
    yield put(actNotif.success('Employee successfully deleted'));
    yield put(actEmployeeList.hideDeleteEmployeeModal());
    yield put(actEmployeeList.setDeletedEmployee(null));
    yield put(actEmployeeList.pageRequest());
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
    yield put(act.sendingRequest(false));
  }
}

export function* assignOutlets(request) {
  const {
    payload: {
      inviteForm: { outlets },
    },
  } = request;
  yield put(actEmployeeList.showAssignOutletModal());
  for (let idx = 0; idx < outlets.length; idx++) {
    const formName = 'invite';
    const fieldName = 'outlets';
    yield put(change(formName, `${fieldName}[${idx}].show`, true));
  }
}

export function* confirmAssignOutlets(request) {
  const {
    payload: { outlets },
  } = request;
  yield put(actEmployeeList.setCheckedOutlets(outlets));
  yield put(actEmployeeList.hideAssignOutletModal());
}

export function* cancelAssignOutlets(request) {
  const {
    payload: { previousOutlets, formName, fieldName },
  } = request;
  for (let idx = 0; idx < previousOutlets.length; idx++) {
    yield put(
      change(
        formName,
        `${fieldName}[${idx}].checked`,
        previousOutlets[idx].checked
      )
    );
    yield put(change(formName, `${fieldName}[${idx}].show`, true));
  }
  yield put(actEmployeeList.hideAssignOutletModal());
}

export function* checkAllOutlets(request) {
  const {
    payload: { fields, formName, fieldName },
  } = request;
  // A little hacky though
  // https://github.com/erikras/redux-form/issues/2058
  for (let idx = 0; idx < fields.length; idx++) {
    if (fields[idx].show) {
      yield put(change(formName, `${fieldName}[${idx}].checked`, true));
    }
  }
}

export function* uncheckAllOutlets(request) {
  const {
    payload: { fields, formName, fieldName },
  } = request;
  // https://github.com/erikras/redux-form/issues/2058
  for (let idx = 0; idx < fields.length; idx++) {
    if (fields[idx].show) {
      yield put(change(formName, `${fieldName}[${idx}].checked`, false));
    }
  }
}

export function* searchOutlet(action) {
  const {
    payload: { outlets, formName, fieldName, keyword },
  } = action;
  for (let idx = 0; idx < outlets.length; idx++) {
    const result = outlets[idx].name.search(new RegExp(keyword, 'i'));
    if (result !== -1) {
      yield put(change(formName, `${fieldName}[${idx}].show`, true));
    } else {
      yield put(change(formName, `${fieldName}[${idx}].show`, false));
    }
  }
}

export function* submitForm(request) {
  const {
    payload: { data },
  } = request;
  try {
    yield put(act.sendingRequest(true));
    yield put(act.showProgressBar());
    yield call(apiEmployee.postEmployee, data);
    yield call(
      apiEmployee.resendInvitation,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      { pending_employee_email: data.email },
      {}
    );
    yield put(actNotif.success('Employee invitation sent.'));
    yield put(actEmployeeList.pageRequest());
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
    yield put(act.sendingRequest(false));
  }
}

export function* cancelAction(cancelledTasks) {
  yield cancel(...cancelledTasks);
}

export default function* saga() {
  yield takeEvery(EMPLOYEE_LIST_SUBMIT_FORM, submitForm);
  const pageReqTask = yield takeLatest(EMPLOYEE_LIST_PAGE_REQUEST, pageRequest);
  const resendInvitationTask = yield takeEvery(
    EMPLOYEE_LIST_RESEND_INVITATION,
    resendInvitation
  );

  yield takeLatest(EMPLOYEE_LIST_CANCEL_DELETE_EMPLOYEE, cancelDeleteEmployee);
  yield takeLatest(EMPLOYEE_LIST_ASK_DELETE_EMPLOYEE, askDeleteEmployee);
  const confirmDelEmployeeTask = yield takeEvery(
    EMPLOYEE_LIST_CONFIRM_DELETE_EMPLOYEE,
    confirmDeleteEmployee
  );

  yield takeEvery(EMPLOYEE_LIST_CHECK_ALL_OUTLETS, checkAllOutlets);
  yield takeEvery(EMPLOYEE_LIST_UNCHECK_ALL_OUTLETS, uncheckAllOutlets);

  const searchOutletTask = yield takeLatest(
    EMPLOYEE_LIST_SEARCH_OUTLET,
    searchOutlet
  );

  yield takeEvery(EMPLOYEE_LIST_ASSIGN_OUTLETS, assignOutlets);
  yield takeEvery(EMPLOYEE_LIST_CONFIRM_ASSIGN_OUTLETS, confirmAssignOutlets);
  yield takeEvery(EMPLOYEE_LIST_CANCEL_ASSIGN_OUTLETS, cancelAssignOutlets);
  yield takeEvery(LOGOUT, cancelAction, [
    pageReqTask,
    resendInvitationTask,
    confirmDelEmployeeTask,
    searchOutletTask,
  ]);
}
